import React, { type ComponentType, Component } from 'react';
import flow from 'lodash/flow';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { AnalyticsSubject } from '@atlassian/jira-analytics-web-react/src/components/decorators.tsx';
import ComponentWithAnalytics from '@atlassian/jira-analytics-web-react/src/utils/component-with-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { injectIntl, type IntlShape } from '@atlassian/jira-intl';
import { toErrorId, toErrorMessage } from '@atlassian/jira-project-settings-apps-common/src/types';
import { logError } from '@atlassian/jira-project-settings-apps-common/src/util/error-logger';
import { PACKAGE_NAME } from '../../model/constants';
import type { AvatarDetails } from '../../model/types';
import { getAccessibleAvatarName } from './accessible-avatar-names';
import messages from './messages';

export type Props = {
	hasFetchAvatarError: boolean;
	selectedAvatar: AvatarDetails | null | undefined;
	avatars: AvatarDetails[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	AvatarPicker: ComponentType<any /* AvatarPickerDialogProps(@atlaskit/media-avatar-picker) */>;
	onCloseAvatarPickerDialog: () => void;
	onSaveImage: (
		arg1: File,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		croppingProperties: any /* CroppingProperties(@atlaskit/media-avatar-picker) */,
		arg3: UIAnalyticsEvent,
	) => void;
	onSavePredefinedAvatar: (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		MediaAvatarPickerAvatar?: any /* MediaAvatarPickerAvatar(@atlaskit/media-avatar-picker) */,
	) => void;
	onShowFetchAvatarsErrorFlag: () => void;
};

type PropsWithIntl = Props & {
	intl: IntlShape;
};

/**
 * Given an avatar selected via the media picker, get the avatar details from the provided
 * list of avatars.
 *
 * @param avatars list of predefined avatars
 * @param mediaPickerAvatar avatar chosen by the media picker
 * @returns {AvatarDetails} to be saved
 */
const getAvatarForSelectedAvatar = (
	avatars: Array<AvatarDetails>,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	mediaPickerAvatar: any /* MediaAvatarPickerAvatar(@atlaskit/media-avatar-picker) */,
): AvatarDetails | null | undefined =>
	avatars.find((avatar: AvatarDetails): boolean => avatar.avatarUrl === mediaPickerAvatar.dataURI);

// eslint-disable-next-line jira/react/no-class-components
export class AvatarPickerDialog extends Component<PropsWithIntl> {
	static defaultProps = {
		selectedAvatar: null,
	};

	componentDidMount() {
		const { hasFetchAvatarError, onShowFetchAvatarsErrorFlag } = this.props;
		if (hasFetchAvatarError) {
			onShowFetchAvatarsErrorFlag();
		}
	}

	onAvatarPicked = (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		avatar: any /* MediaAvatarPickerAvatar(@atlaskit/media-avatar-picker) */,
	): void => {
		const { avatars, onSavePredefinedAvatar } = this.props;

		const jiraAvatar: AvatarDetails | null | undefined = getAvatarForSelectedAvatar(
			avatars,
			avatar,
		);

		if (jiraAvatar === null || jiraAvatar === undefined) {
			// This is a bug between the integration of Media Avatar Picker Dialog and the Project Details Avatar
			logError(
				PACKAGE_NAME,
				toErrorId('avatarPickerDialog'),
				toErrorMessage('Attempted to save a predefined avatar but could not find it'),
			);
		} else {
			onSavePredefinedAvatar(jiraAvatar);
		}
	};

	render() {
		const { selectedAvatar, avatars, intl, AvatarPicker, onSaveImage, onCloseAvatarPickerDialog } =
			this.props;

		const { formatMessage } = intl;

		const mediaPickerAvatars = avatars.map((avatar) => ({
			dataURI: avatar.avatarUrl,
			name: fg('hix-5289-fix-enghealth-a11y-violations-helix')
				? avatar?.name || getAccessibleAvatarName(avatar)(intl)
				: getAccessibleAvatarName(avatar)(intl),
		}));

		const selectedMediaPickerAvatar = mediaPickerAvatars.find(
			(avatar) => selectedAvatar && selectedAvatar.avatarUrl === avatar.dataURI,
		);

		return (
			<AvatarPicker
				defaultSelectedAvatar={selectedMediaPickerAvatar}
				avatars={mediaPickerAvatars}
				onCancel={onCloseAvatarPickerDialog}
				onImagePicked={onSaveImage}
				onAvatarPicked={this.onAvatarPicked}
				primaryButtonText={formatMessage(messages.selectAvatar)}
				title={formatMessage(messages.dialogTitle)}
				predefinedAvatarsText={formatMessage(messages.defaultListTitle)}
				showMoreAvatarsButtonLabel={formatMessage(messages.moreIconsButtonLabel)}
			/>
		);
	}
}

export default flow(
	injectIntl,
	ComponentWithAnalytics('projectAvatar', {
		onSaveImage: 'updated',
	}),
	AnalyticsSubject('projectAvatar'),
)(AvatarPickerDialog);
